import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Package, Hash, Banknote } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { helper } from "@/lib/helper";
import { cn } from "@/lib/utils";

type AssetRow = {
  id?: number | string;
  itemDesc?: string;
  serialNo?: string;
  itemValue?: number | string;
};

type PaginationMeta = {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
  has_more: boolean;
};

export default function CustomerAssetsTab({ customerId }: { customerId: string }) {
  const perPage = 10;

  const [rows, setRows] = useState<AssetRow[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [page, setPage] = useState(1);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  function normalize(res: any): { rows: AssetRow[]; meta: PaginationMeta | null } {
    const payload = res?.data ?? res;

    // sometimes apiFetch returns array directly
    if (Array.isArray(payload)) return { rows: payload, meta: null };

    const rows =
      (Array.isArray(payload?.data) && payload.data) ||
      (Array.isArray(payload?.data?.data) && payload.data.data) ||
      [];

    const meta = payload?.meta ?? payload?.data?.meta ?? null;

    return { rows, meta };
  }

  async function fetchAssets(p = 1) {
    if (!customerId) return;
    if (loading) return;

    setLoading(true);
    setErr("");

    try {
      const res = await apiFetch<any>(
        `/customer-profile/${customerId}/assets?page=${p}&per_page=${perPage}`
      );

      const out = normalize(res);
      setRows(out.rows);
      setMeta(out.meta);
      setPage(p);
    } catch (e: any) {
      setErr(e?.message || "Failed to load assets");
      setRows([]);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchAssets(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const totalValue = useMemo(
    () => rows.reduce((sum, r) => sum + (Number(r.itemValue) || 0), 0),
    [rows]
  );

  const canPrev = page > 1 && !loading;
  const canNext = Boolean(meta?.has_more) && !loading;

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm font-semibold">Customer Assets</div>

        <Button
          size="sm"
          variant="outline"
          onClick={() => fetchAssets(1)}
          disabled={loading}
          className="gap-2"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Refresh
        </Button>
      </div>

      {/* Error */}
      {err ? (
        <Card>
          <CardContent className="p-4 text-sm text-destructive">{err}</CardContent>
        </Card>
      ) : null}

      {/* Desktop table */}
      <div className="hidden lg:block overflow-x-auto rounded-xl border">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="bg-muted/50">
            <tr className="text-left">
              <th className="p-3">#</th>
              <th className="p-3">Item</th>
              <th className="p-3">Serial</th>
              <th className="p-3">Value</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={4} className="p-6 text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading assets...
                  </div>
                </td>
              </tr>
            ) : rows.length ? (
              rows.map((r, idx) => (
                <tr key={String(r.id ?? idx)} className="border-t">
                  <td className="p-3">{idx + 1}</td>
                  <td className="p-3 font-medium">{helper.safe(r.itemDesc)}</td>
                  <td className="p-3">{helper.safe(r.serialNo)}</td>
                  <td className="p-3 font-semibold text-primary">{helper.nf(r.itemValue)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={4} className="p-6 text-muted-foreground">
                  No assets found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="lg:hidden space-y-3">
        {loading ? (
          <Card className="shadow-card">
            <CardContent className="p-4 text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading assets...
            </CardContent>
          </Card>
        ) : rows.length ? (
          rows.map((r, idx) => (
            <Card key={String(r.id ?? idx)} className="shadow-card rounded-2xl border bg-background">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="h-9 w-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                        <Package className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold truncate">{helper.safe(r.itemDesc, "Asset")}</div>
                        <div className="text-xs text-muted-foreground inline-flex items-center gap-1">
                          <Hash className="h-3.5 w-3.5" />
                          {helper.safe(r.serialNo, "—")}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={cn("text-primary font-bold whitespace-nowrap")}>
                    {helper.nf(r.itemValue)}
                  </div>
                </div>

                <div className="rounded-xl border bg-muted/20 p-3 flex items-center justify-between">
                  <div className="text-xs text-muted-foreground inline-flex items-center gap-1">
                    <Banknote className="h-3.5 w-3.5 text-primary" />
                    Value
                  </div>
                  <div className="text-sm font-semibold">{helper.nf(r.itemValue)}</div>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <div className="text-sm text-muted-foreground">No assets found.</div>
        )}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs text-muted-foreground">
          {meta ? `Page ${meta.current_page} of ${meta.last_page} • Total ${meta.total}` : ""}
        </div>

        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" disabled={!canPrev} onClick={() => fetchAssets(page - 1)}>
            Prev
          </Button>

          <Button size="sm" variant="outline" disabled={!canNext} onClick={() => fetchAssets(page + 1)}>
            Next
          </Button>
        </div>
      </div>

      {/* Total */}
      {rows.length ? (
        <Card className="shadow-card">
          <CardContent className="p-3 flex items-center justify-between">
            <div className="text-sm font-semibold">Total Value</div>
            <div className="text-sm font-bold text-primary">{helper.nf(totalValue)}</div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}